import './laxhjalpen-guardian-approval-page.scss';
import { ReactNode, useState } from 'react';
import { BizIcon } from '../../../../../FrontEndReact/src/Core/Components/Mini/BizIcon';
import { ParticipationStatus } from '../../../../../FrontEndReact/src/Core/Types/event';
import { useGetGuardianApprovalEvent, useUpdateGuardianApproval } from '../Services/laxhjalpenGuardianApproval';

const LaxhjalpenGuardianApprovalPage = () => {
	const encrypted = getApprovalCode();
	const [decision, setDecision] = useState<boolean | null>(null);
	const [pendingDecision, setPendingDecision] = useState<boolean | null>(null);
	const eventQuery = useGetGuardianApprovalEvent(encrypted);
	const update = useUpdateGuardianApproval(encrypted);
	const approvalEvent = update.data ?? eventQuery.data;
	const isLoading = eventQuery.statusState === 'LOADING' || eventQuery.statusState === 'INITIAL';
	const isUpdating = update.statusState === 'LOADING';

	if (!encrypted) {
		return <PageState type='error' title='Länken saknar godkännandekod.' text='Öppna länken från mejlet igen.' />;
	}

	if (isLoading) {
		return (
			<PageState type='loading' title='Hämtar aktivitet'>
				<BizIcon icon='LOADING' />
			</PageState>
		);
	}

	if (eventQuery.statusState === 'ERROR' || !approvalEvent) {
		return (
			<PageState
				type='error'
				title='Det gick inte att visa aktiviteten.'
				text={getErrorText(eventQuery.errorType, eventQuery.errorMessage)}
			/>
		);
	}

	const statusInfo = getStatusInfo(approvalEvent.studentParticipationStatus);
	const currentDecision = getCurrentDecision(approvalEvent.studentParticipationStatus);
	const canRespond = currentDecision !== 'unknown';
	const hasSubmittedDecision = decision !== null && update.statusState !== 'ERROR';

	return (
		<section className='biz-lh-guardian-approval-page'>
			<div className='biz-lh-guardian-approval-page__shell'>
				<div className='biz-lh-guardian-approval-page__header'>
					<div>
						<p className='biz-lh-guardian-approval-page__eyebrow'>Godkännande av aktivitet</p>
						<h1>{approvalEvent.eventName}</h1>
					</div>
					<span
						className={`biz-lh-guardian-approval-page__status biz-lh-guardian-approval-page__status--${statusInfo.type}`}
					>
						{statusInfo.label}
					</span>
				</div>

				<div className='biz-lh-guardian-approval-page__details'>
					<DetailItem iconClass='fa-regular fa-user' label='Elev' value={approvalEvent.studentName} />
					<DetailItem
						iconClass='fa-regular fa-calendar'
						label='Datum'
						value={formatDateTime(approvalEvent.eventStartDateTimeUtc)}
					/>
					<DetailItem
						iconClass='fa-regular fa-clock'
						label='Svara senast'
						value={formatDateTime(approvalEvent.eventRsvpDateTimeUtc)}
					/>
					{approvalEvent.eventLocation && (
						<DetailItem iconClass='fa-regular fa-location-dot' label='Plats' value={approvalEvent.eventLocation} />
					)}
				</div>

				{hasSubmittedDecision && (
					<div className='biz-lh-guardian-approval-page__message biz-lh-guardian-approval-page__message--success'>
						<BizIcon icon='CHECK' />
						<span>
							{decision
								? 'Tack, eleven är nu godkänd för aktiviteten.'
								: 'Tack, vi har registrerat att eleven inte deltar.'}
						</span>
					</div>
				)}

				{update.statusState === 'ERROR' && (
					<div className='biz-lh-guardian-approval-page__message biz-lh-guardian-approval-page__message--error'>
						<BizIcon icon='TRIANGLE_EXCLAMATION' />
						<span>{getErrorText(update.errorType, update.errorMessage)}</span>
					</div>
				)}

				{canRespond ? (
					<div className='biz-lh-guardian-approval-page__actions'>
						<button
							type='button'
							className={`biz-lh-guardian-approval-page__button biz-lh-guardian-approval-page__button--decline ${
								currentDecision === false ? 'biz-lh-guardian-approval-page__button--selected' : ''
							}`}
							disabled={isUpdating}
							onClick={() => submitDecision(false, update.updateGuardianApproval, setDecision, setPendingDecision)}
						>
							<BizIcon icon={isUpdating && pendingDecision === false ? 'LOADING' : 'REMOVE'} />
							<span>{currentDecision === false ? 'Deltar inte' : 'Tacka nej'}</span>
						</button>
						<button
							type='button'
							className={`biz-lh-guardian-approval-page__button biz-lh-guardian-approval-page__button--approve ${
								currentDecision === true ? 'biz-lh-guardian-approval-page__button--selected' : ''
							}`}
							disabled={isUpdating}
							onClick={() => submitDecision(true, update.updateGuardianApproval, setDecision, setPendingDecision)}
						>
							<BizIcon icon={isUpdating && pendingDecision === true ? 'LOADING' : 'CHECK'} />
							<span>{currentDecision === true ? 'Godkänd' : 'Godkänn'}</span>
						</button>
					</div>
				) : (
					<p className='biz-lh-guardian-approval-page__closed'>
						Svaret är redan registrerat eller svarstiden har passerat.
					</p>
				)}
			</div>
		</section>
	);
};

export default LaxhjalpenGuardianApprovalPage;

function DetailItem({ iconClass, label, value }: { iconClass: string; label: string; value: string }) {
	return (
		<div className='biz-lh-guardian-approval-page__detail'>
			<span className='biz-lh-guardian-approval-page__detail-icon-frame'>
				<i className={`biz-lh-guardian-approval-page__detail-icon ${iconClass}`} aria-hidden='true' />
			</span>
			<div>
				<span>{label}</span>
				<strong>{value}</strong>
			</div>
		</div>
	);
}

function PageState({
	type,
	title,
	text,
	children,
}: {
	type: 'loading' | 'error';
	title: string;
	text?: string;
	children?: ReactNode;
}) {
	return (
		<section className={`biz-lh-guardian-approval-page biz-lh-guardian-approval-page--${type}`}>
			<div className='biz-lh-guardian-approval-page__state'>
				{children}
				<h1>{title}</h1>
				{text && <p>{text}</p>}
			</div>
		</section>
	);
}

async function submitDecision(
	willAttend: boolean,
	updateGuardianApproval: (willAttend: boolean) => Promise<unknown>,
	setDecision: (decision: boolean) => void,
	setPendingDecision: (decision: boolean | null) => void
) {
	setPendingDecision(willAttend);

	try {
		await updateGuardianApproval(willAttend);
		setDecision(willAttend);
	} finally {
		setPendingDecision(null);
	}
}

function getApprovalCode() {
	if (typeof window === 'undefined') {
		return '';
	}

	return new URLSearchParams(window.location.search).get('c') ?? '';
}

function formatDateTime(value: string) {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return new Intl.DateTimeFormat('sv-SE', {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);
}

function getStatusInfo(status: ParticipationStatus) {
	switch (status) {
		case ParticipationStatus.Invited:
			return { label: 'Inväntar svar', type: 'pending' };
		case ParticipationStatus.ConfirmedAttended:
		case ParticipationStatus.Attending:
			return { label: 'Godkänd', type: 'approved' };
		case ParticipationStatus.NotAttending:
			return { label: 'Deltar inte', type: 'declined' };
		default:
			return { label: 'Ej aktuell', type: 'neutral' };
	}
}

function getCurrentDecision(status: ParticipationStatus): boolean | null | 'unknown' {
	switch (status) {
		case ParticipationStatus.Invited:
			return null;
		case ParticipationStatus.ConfirmedAttended:
		case ParticipationStatus.Attending:
			return true;
		case ParticipationStatus.NotAttending:
			return false;
		default:
			return 'unknown';
	}
}

function getErrorText(errorType: string | null, errorMessage: string | null) {
	if (errorType === 'FORBIDDEN') {
		return 'Länken kan ha gått ut, aktiviteten kan ha passerat eller så saknas behörighet.';
	}

	if (errorType === 'NOT_FOUND') {
		return 'Aktiviteten, eleven eller vårdnadshavaren kunde inte hittas.';
	}

	return errorMessage || 'Försök igen senare eller kontakta Läxhjälpen.';
}

